import { Label } from '@/components/ui/label'
import { SubtextState, subtextHelpers } from '@/components/ui/subtext'
import { cn } from '@/lib/utils'
import { useState, useCallback } from 'react'

interface FieldShellProps {
	label?: string
	labelClassName?: string
	children: React.ReactNode
	subtextStrategy?: (state: SubtextState) => { text?: React.ReactNode; visibility: 'always' | 'hover' | 'focus' | 'hoverOrFocus' | 'never' }
	className?: string
	disabled?: boolean
}

export function FieldShell({
	label,
	labelClassName,
	children,
	subtextStrategy,
	className,
	disabled = false,
}: FieldShellProps) {
	const [isFocused, setIsFocused] = useState(false)
	const [isHovered, setIsHovered] = useState(false)

	const handleFocus = useCallback(() => setIsFocused(true), [])
	const handleBlur = useCallback(() => setIsFocused(false), [])
	const handleMouseEnter = useCallback(() => setIsHovered(true), [])
	const handleMouseLeave = useCallback(() => setIsHovered(false), [])

	// Calculate subtext based on strategy
	const subtextState: SubtextState = {
		focused: isFocused,
		hovered: isHovered,
		disabled,
		value: '', // This would need to be passed from the field if needed
	}

	const subtextResult = subtextStrategy?.(subtextState) || { text: undefined, visibility: 'never' as const }
	const shouldShowSubtext = subtextHelpers.shouldShow(subtextResult, subtextState)

	return (
		<div className={cn("space-y-2", className)}>
			{label && (
				<Label className={cn("text-sm", labelClassName)}>
					{label}
				</Label>
			)}
			<div className="space-y-0">
				<div
					onFocus={handleFocus}
					onBlur={handleBlur}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
				>
					{children}
				</div>
				{/* Subtext area - match InputField exact spacing */}
				<div className="mt-0.5 h-[18px] overflow-hidden">
					<p
						className={cn(
							"text-[0.88rem] italic leading-[1.05rem] text-foreground/70 transition-opacity duration-200 truncate",
							shouldShowSubtext ? "opacity-100" : "opacity-0"
						)}
					>
						{subtextResult.text ?? '\u200b'}
					</p>
				</div>
			</div>
		</div>
	)
}
